import React, { useEffect, useState } from "react";
import { useNavPillsIndicator } from 'hooks/useNavPillsIndicator';
import "bootstrap-select";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import RetailPartnerService from "service/RetailPartnerService/RetailPartnerService";
import LoadingSpinner from "component/LoadingSpinner";
import { hasRetailPartnerAccess } from '../../helpers/hasPrivilege';
import MerchantPartnerOwnerDetailModal from "component/retailPartnerDetailModal/RetailPartnerOwnerDetailModal";
import RetailPartnerStoreDetailModal from "component/retailPartnerDetailModal/RetailPartnerStoreDetailModal";
import CustomDropdown from "component/CustomDropdown";
import { PageSizeOption } from "domain/constant/PageSizeOption";

const RetailPartner: React.FC = () => {
  const { profile } = useSelector((state: any) => state.auth);
  const [data, setData] = useState<any[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadingExportCsv, setLoadingExportCsv] = useState(false);
  const [viewMode, setViewMode] = useState<'store' | 'user'>('store');
  const { navRef: viewModeNavRef, indicatorStyle: viewModeIndicatorStyle } = useNavPillsIndicator(viewMode);
  const [showOwnerDetailModal, setShowOwnerDetailModal] = useState(false);
  const [selectedOwnerId, setSelectedOwnerId] = useState<string | null>(null);
  const [showStoreDetailModal, setShowStoreDetailModal] = useState(false);
  const [selectedStoreId, setSelectedStoreId] = useState<string | null>(null);

  const canCreate = hasRetailPartnerAccess(profile, 'create');
  const canUpdate = hasRetailPartnerAccess(profile, 'update');
  const canDelete = hasRetailPartnerAccess(profile, 'delete');

  const retailPartnerService = RetailPartnerService();

  const fetchData = async () => {
    const token = profile?.data.token;
    if (!token) {
      window.alert('Token tidak ditemukan. Silakan login kembali.');
      return;
    }

    setLoading(true);
    try {
      const res = await retailPartnerService.getList(
        token,
        currentPage,
        pageSize,
        searchTerm
      );
      setData(res.data ?? []);
      setTotalPages(res.pagination?.totalPage ?? 0);
      setTotalRecords(res.pagination?.totalRecords ?? 0);
    } catch (err: any) {
      console.error(err);
      const errorMessage = err.response?.data?.message || err.message || 'Terjadi kesalahan saat mengambil data retail partner';
      window.alert(`Error: ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  };

  const fetchDataByOwner = async () => {
    const token = profile?.data.token;
    if (!token) {
      window.alert('Token tidak ditemukan. Silakan login kembali.');
      return;
    }

    setLoading(true);
    try {
      const res = await retailPartnerService.getListByOwner(
        token,
        currentPage,
        pageSize,
        searchTerm
      );
      setData(res.data ?? []);
      setTotalPages(res.pagination?.totalPage ?? 0);
      setTotalRecords(res.pagination?.totalRecords ?? 0)
    } catch (err: any) {
      console.error(err);
      const errorMessage = err.response?.data?.message || err.message || 'Terjadi kesalahan saat mengambil data owner';
      window.alert(`Error: ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (viewMode === 'store') {
      fetchData();
    } else {
      fetchDataByOwner();
    }
  }, [currentPage, pageSize, viewMode]);

  const handleSearchClick = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setCurrentPage(1);
    if (viewMode === 'store') {
      fetchData();
    } else {
      fetchDataByOwner();
    }
  };

  const handleDelete = async (id: string, storeName: string) => {
    const token = profile?.data.token;
    if (!token) {
      window.alert('Token tidak ditemukan. Silakan login kembali.');
      return;
    }

    const confirmDelete = window.confirm(
      `Apakah Anda yakin ingin menghapus Retail Partner "${storeName}"?`
    );
    if (!confirmDelete) return;

    try {
      await retailPartnerService.remove(token, id);
      alert(`Retail Partner "${storeName}" berhasil dihapus`);
      fetchData();
    } catch (err: any) {
      console.error(err);
      const errorMessage = err.response?.data?.message || err.message || 'Terjadi kesalahan saat menghapus retail partner';
      window.alert(`Error: ${errorMessage}`);
    }
  };

  const handleViewOwnerDetail = (ownerId: string) => {
    setSelectedOwnerId(ownerId);
    setShowOwnerDetailModal(true);
  };

  const handleCloseOwnerDetailModal = () => {
    setShowOwnerDetailModal(false);
    setSelectedOwnerId(null);
  };

  const handleViewStoreDetail = (storeId: string) => {
    setSelectedStoreId(storeId);
    setShowStoreDetailModal(true);
  };

  const handleCloseStoreDetailModal = () => {
    setShowStoreDetailModal(false);
    setSelectedStoreId(null);
  };

  //   const handleExportToCsv = async () => {
  //     const token = profile?.data.token;
  //     if (!token) return;

  //     setLoadingExportCsv(true);
  //     try {
  //       const response = await retailPartnerService.exportToCsv(token);

  //       const url = window.URL.createObjectURL(new Blob([response.data]));
  //       const link = document.createElement('a');
  //       link.href = url;

  //       const contentDisposition = response.headers['content-disposition'];
  //       const fileNameMatch = contentDisposition?.match(/filename="?([^"]+)"?/);

  //       if (!fileNameMatch || !fileNameMatch[1]) {
  //         throw new Error('Filename not found in Content-Disposition header');
  //       }

  //       const fileName = decodeURIComponent(fileNameMatch[1]);

  //       link.setAttribute('download', fileName);
  //       document.body.appendChild(link);
  //       link.click();

  //       link.remove();
  //       window.URL.revokeObjectURL(url);
  //       setLoadingExportCsv(false);
  //     } catch (error) {
  //       console.error('Error downloading CSV:', error);
  //       setLoadingExportCsv(false);
  //     }
  //   };

  return (
    <div className="page-wrapper">
      <div className="row">
        <div className="col-12">
          <div className="d-flex">
            <h5 style={{ flex: 1 }}>Retail Partners</h5>
            <div>
              <p>TransMore Retail Partners</p>
            </div>
          </div>
        </div>
        <div className="col-12">
          <div className="p-4" style={{ background: "white" }}>
            <div className="d-flex mb-3" style={{ flex: 1 }}>
              <div ref={viewModeNavRef} className="nav-pills-animated h-100">
                <div className="nav-pill-indicator" style={viewModeIndicatorStyle} />
                <button
                  type="button"
                  className={`nav-link ${viewMode === 'store' ? 'active' : ''}`}
                  onClick={() => {
                    setViewMode('store');
                    setCurrentPage(1);
                  }}
                >
                  By Store
                </button>
                <button
                  type="button"
                  className={`nav-link ${viewMode === 'user' ? 'active' : ''}`}
                  onClick={() => {
                    setViewMode('user');
                    setCurrentPage(1);
                  }}
                >
                  By User
                </button>
              </div>
            </div>

            <div className="d-flex mb-3">
              <form className="d-flex" onSubmit={handleSearchClick}>
                <input
                  type="text"
                  className="form-control form-control-compact me-2"
                  placeholder="Search..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                <button type="submit" className="btn btn-primary px-4">
                  <i className="fa-solid fa-search"></i>
                </button>
              </form>

              {/* <div className="d-flex justify-content-end" style={{ flex: 1 }}>
                {canCreate && (
                  <>
                    <button className="btn btn-outline-primary me-2">
                      Import From CSV
                    </button>
                  </>
                )}
                <button
                  className="btn btn-outline-primary me-2 d-flex justify-content-center align-items-center"
                  //   onClick={handleExportToCsv}
                  disabled={loadingExportCsv}
                >
                  {loadingExportCsv && (
                    <>
                      <LoadingSpinner />
                      <p className="ms-2"></p>
                    </>
                  )}
                  Export To CSV
                </button>
                {canCreate && (
                  <>
                    <Link
                      to="/retail-partner/0"
                      className="float-end text-decoration-none tm-text-primary"
                    >
                      <button className="btn btn-primary">
                        <span className="me-2">Add</span>
                        <i className="fa-solid fa-plus"></i>
                      </button>
                    </Link>
                  </>
                )}
              </div> */}
            </div>

            <table className="table table-condensed table-hover">
              <thead className="table-primary">
                <tr>
                  <th>ID</th>
                  {viewMode === 'store' ? (
                    <>
                      <th>Store Name</th>
                      <th>Owner</th>
                      <th>Email</th>
                      <th>Phone</th>
                      <th>Address</th>
                      <th>Landmark</th>
                      <th>Reference</th>
                      <th>Latitude</th>
                      <th>Longitude</th>
                    </>
                  ) : (
                    <>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Phone</th>
                      <th>Store List</th>
                    </>
                  )}
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {!loading ? (
                  data.length > 0 ? (
                    data.map((item) => (
                      <tr key={item.id}>
                        <td>{item.id}</td>
                        {viewMode === 'store' ? (
                          <>
                            <td>{item.storeName}</td>
                            <td>{item.ownerName}</td>
                            <td>{item.ownerEmail}</td>
                            <td>{item.ownerPhone}</td>
                            <td>{item.address}</td>
                            <td>{item.landmark ?? "-"}</td>
                            <td>{item.reference ?? "-"}</td>
                            <td>{item.latitude ?? "-"}</td>
                            <td>{item.longitude ?? "-"}</td>
                          </>
                        ) : (
                          <>
                            <td>{item.name}</td>
                            <td>{item.email}</td>
                            <td>{item.phone}</td>
                            <td>
                              {item.storeList && item.storeList.length > 0 ? (
                                <ul className="list-unstyled mb-0">
                                  {item.storeList.map((store: any, index: number) => (
                                    <li key={store.id}>
                                      {index > 0 && ", "}
                                      {store.name}
                                    </li>
                                  ))}
                                </ul>
                              ) : (
                                "-"
                              )}
                            </td>
                          </>
                        )}
                        <td>
                          <div className="d-flex">
                            {viewMode === 'store' && (
                              <button
                                className="btn btn-outline-info btn-small-compact border-0"
                                onClick={() => handleViewStoreDetail(item.id)}
                                title="View Detail"
                              >
                                <i className="fa-solid fa-eye"></i>
                              </button>
                            )}

                            {viewMode === 'user' && (
                              <button
                                className="btn btn-outline-info btn-small-compact border-0"
                                onClick={() => handleViewOwnerDetail(item.id)}
                                title="View Detail"
                              >
                                <i className="fa-solid fa-eye"></i>
                              </button>
                            )}
                            {canUpdate && (
                              <>
                                <Link to={"/retail-partner/" + item.id}>
                                  <button className="btn btn-outline-warning btn-small-compact border-0">
                                    <i className="fa-solid fa-edit"></i>
                                  </button>
                                </Link>
                              </>
                            )}
                            {canDelete && (
                              <button
                                className="btn btn-outline-danger btn-small-compact border-0 ms-2"
                                onClick={() =>
                                  handleDelete(
                                    item.id,
                                    viewMode === 'store' ? item.storeName : item.name
                                  )
                                }
                              >
                                <i className="fa-solid fa-trash"></i>
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={viewMode === 'store' ? 11 : 6} className="text-center">
                        Tidak ada data
                      </td>
                    </tr>
                  )
                ) : (
                  <tr>
                    <td colSpan={viewMode === 'store' ? 11 : 6} className="text-center">
                      <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>

            <div className="d-flex justify-content-end align-items-center mt-3">
              <div className="d-flex align-items-center">
                <div className="me-4 d-flex align-items-center">
                  <label className="me-2">Rows per page:</label>
                  <CustomDropdown
                    value={pageSize}
                    options={PageSizeOption.options}
                    onChange={(opt) => {
                      setPageSize(Number(opt.id));
                      setCurrentPage(1);
                    }}
                    align="top"
                  />
                </div>
                <span className="me-3">
                  {`${(currentPage - 1) * pageSize + 1}–${Math.min(
                    currentPage * pageSize,
                    totalRecords
                  )} of ${totalRecords}`}
                </span>

                <nav>
                  <ul className="pagination mb-0">
                    <li
                      className={`page-item ${currentPage === 1 ? "disabled" : ""
                        }`}
                    >
                      <button
                        className="page-link"
                        onClick={() => setCurrentPage(1)}
                        style={{ border: 0 }}
                      >
                        <i className="fa fa-angle-double-left"></i>
                      </button>
                    </li>
                    <li
                      className={`page-item ${currentPage === 1 ? "disabled" : ""
                        }`}
                    >
                      <button
                        className="page-link"
                        onClick={() => setCurrentPage(currentPage - 1)}
                        style={{ border: 0 }}
                      >
                        <i className="fa fa-angle-left"></i>
                      </button>
                    </li>

                    <li className="page-item disabled">
                      <span className="page-link" style={{ border: 0 }}>
                        {currentPage}
                      </span>
                    </li>

                    <li
                      className={`page-item ${currentPage === totalPages ? "disabled" : ""
                        }`}
                    >
                      <button
                        className="page-link"
                        onClick={() => setCurrentPage(currentPage + 1)}
                        style={{ border: 0 }}
                      >
                        <i className="fa fa-angle-right"></i>
                      </button>
                    </li>
                    <li
                      className={`page-item ${currentPage === totalPages ? "disabled" : ""
                        }`}
                    >
                      <button
                        className="page-link"
                        onClick={() => setCurrentPage(totalPages)}
                        style={{ border: 0 }}
                      >
                        <i className="fa fa-angle-double-right"></i>
                      </button>
                    </li>
                  </ul>
                </nav>
              </div>
            </div>
          </div>
        </div>
      </div>
      {showStoreDetailModal && (
        <RetailPartnerStoreDetailModal
          show={showStoreDetailModal}
          onClose={handleCloseStoreDetailModal}
          storeId={selectedStoreId || undefined}
        />
      )}

      {showOwnerDetailModal && (
        <MerchantPartnerOwnerDetailModal
          show={showOwnerDetailModal}
          onClose={handleCloseOwnerDetailModal}
          ownerId={selectedOwnerId || undefined}
        />
      )}
    </div>
  );
};

export default RetailPartner;
